import type {
  AnchoredRow,
  AnchoredRowSceneMetadata,
  CollationRowTitle,
  CollationRowTitleLabel,
  CollationRowTitleOrigin,
  CollationRowTitleProvenance,
} from '../types/anchoredRowTypes'

export const COLLATION_ROW_TITLE_SCHEMA_VERSION = 1 as const
export const COLLATION_ROW_TITLE_SIDECAR_FORMAT_V1 = 'collation_row_title_sidecar.v1'
export const COLLATION_ROW_TITLE_SIDECAR_FORMAT = 'collation_row_title_sidecar.v2'
export const MAX_COLLATION_ROW_TITLE_CHARACTERS = 500
const EMPTY_SCENE_TITLES = new Set(['empty'])

export interface CollationRowTitleDraft {
  primaryText: string
  primaryLanguage?: string
  parallelText?: string
  parallelLanguage?: string
  origin: CollationRowTitleOrigin
  provenance?: CollationRowTitleProvenance
  updatedBy?: string
}

export interface CollationRowTitleUpdate extends CollationRowTitleDraft {
  rowId: string
}

export interface CollationRowTitleApplyResult {
  updatedRows: AnchoredRow[]
  changedRowIds: string[]
  unchangedRowIds: string[]
  unknownRowIds: string[]
}

export interface CollationRowTitleSidecarRow {
  row_order: number
  row_label: string
  source_row_order?: number
  source_unit_id: string
  title: string
}

export interface CollationRowTitleSidecarScene {
  scene_order: number
  source_row_order: number
  source_unit_id: string
  title: string
  row_start: number
  row_end: number
  row_count: number
  start_row_label: string
  end_row_label: string
  start_source_unit_id: string
}

export interface CollationRowTitleSidecar {
  format_version: typeof COLLATION_ROW_TITLE_SIDECAR_FORMAT | typeof COLLATION_ROW_TITLE_SIDECAR_FORMAT_V1
  generated_at?: string
  source_html?: string
  source_html_name: string
  source_html_sha256: string
  chapter_code: string
  collation_title?: string
  row_count?: number
  rows: CollationRowTitleSidecarRow[]
  scenes: CollationRowTitleSidecarScene[]
}

export type CollationRowTitleSidecarMatchMethod = 'source-unit-id' | 'row-label' | 'row-order'
export type CollationRowTitleSidecarMatchStatus = 'ready' | 'same' | 'conflict' | 'ambiguous' | 'unmatched'

export interface CollationRowTitleSidecarMatch {
  sidecarRow: CollationRowTitleSidecarRow
  status: CollationRowTitleSidecarMatchStatus
  method: CollationRowTitleSidecarMatchMethod | null
  rowId: string | null
  candidateRowIds: string[]
  currentTitle: string
  safeToReplace: boolean
}

export interface CollationRowTitleSidecarSceneMatch {
  sidecarScene: CollationRowTitleSidecarScene
  status: CollationRowTitleSidecarMatchStatus
  method: CollationRowTitleSidecarMatchMethod | 'mixed' | null
  rowIds: string[]
  startRowId: string | null
  endRowId: string | null
  currentTitles: string[]
  safeToReplace: boolean
}

export interface CollationRowTitleSidecarPreview {
  sidecar: CollationRowTitleSidecar
  matches: CollationRowTitleSidecarMatch[]
  counts: Record<CollationRowTitleSidecarMatchStatus, number>
  sceneMatches: CollationRowTitleSidecarSceneMatch[]
  sceneCounts: Record<CollationRowTitleSidecarMatchStatus, number>
  repairableTitleCount: number
  repairableSceneCount: number
  staleImportedTitleRowIds: string[]
  staleImportedSceneRowIds: string[]
  canClearStaleImports: boolean
}

export interface CollationRowTitleSidecarRepairResult {
  updatedRows: AnchoredRow[]
  changedRowIds: string[]
  appliedTitleRowIds: string[]
  clearedTitleRowIds: string[]
  appliedSceneRowIds: string[]
  clearedSceneRowIds: string[]
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
)

const cleanText = (value: unknown): string => String(value || '').replace(/\s+/g, ' ').trim()

const boundedTitleText = (value: unknown, field: string): string => {
  const text = cleanText(value)
  if (!text) {
    throw new Error(`${field} must not be empty`)
  }
  if (text.length > MAX_COLLATION_ROW_TITLE_CHARACTERS) {
    throw new Error(`${field} exceeds ${MAX_COLLATION_ROW_TITLE_CHARACTERS} characters`)
  }
  return text
}

const cleanLanguage = (value: unknown): string | undefined => {
  const language = cleanText(value)
  return language || undefined
}

const sameLabels = (left: CollationRowTitleLabel[], right: CollationRowTitleLabel[]): boolean => (
  left.length === right.length
  && left.every((label, index) => (
    label.text === right[index].text
    && label.language === right[index].language
    && label.role === right[index].role
  ))
)

export const buildCollationRowTitle = (
  draft: CollationRowTitleDraft,
  existing?: CollationRowTitle,
  now = new Date().toISOString(),
): CollationRowTitle => {
  const labels: CollationRowTitleLabel[] = [{
    text: boundedTitleText(draft.primaryText, 'Primary title'),
    language: cleanLanguage(draft.primaryLanguage),
    role: 'primary',
  }]
  const parallelText = cleanText(draft.parallelText)
  if (parallelText) {
    labels.push({
      text: boundedTitleText(parallelText, 'Parallel title'),
      language: cleanLanguage(draft.parallelLanguage),
      role: 'parallel',
    })
  }
  return {
    schemaVersion: COLLATION_ROW_TITLE_SCHEMA_VERSION,
    labels,
    origin: draft.origin,
    ...(draft.provenance ? { provenance: { ...draft.provenance } } : {}),
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    ...(draft.updatedBy ? { updatedBy: draft.updatedBy } : {}),
  }
}

export const getCollationRowTitle = (row: AnchoredRow): CollationRowTitle | null => {
  const title = row.metadata?.rowTitle
  if (
    !title
    || title.schemaVersion !== COLLATION_ROW_TITLE_SCHEMA_VERSION
    || !Array.isArray(title.labels)
  ) {
    return null
  }
  const labels = title.labels
    .map(label => ({
      text: cleanText(label?.text),
      language: cleanLanguage(label?.language),
      role: label?.role === 'parallel' ? 'parallel' as const : 'primary' as const,
    }))
    .filter(label => Boolean(label.text))
  if (!labels.length) {
    return null
  }
  return {
    ...title,
    labels,
  }
}

export const getCollationRowPrimaryTitle = (row: AnchoredRow): string => {
  const title = getCollationRowTitle(row)
  return title?.labels.find(label => label.role === 'primary')?.text || title?.labels[0]?.text || ''
}

export const getCollationRowParallelTitle = (row: AnchoredRow): string => {
  const title = getCollationRowTitle(row)
  return title?.labels.find(label => label.role === 'parallel')?.text || ''
}

export const applyCollationRowTitleUpdates = ({
  rows,
  updates,
  now = new Date().toISOString(),
}: {
  rows: AnchoredRow[]
  updates: CollationRowTitleUpdate[]
  now?: string
}): CollationRowTitleApplyResult => {
  const rowIdSet = new Set(rows.map(row => row.id))
  const updateByRowId = new Map<string, CollationRowTitleUpdate>()
  updates.forEach(update => {
    if (!updateByRowId.has(update.rowId)) {
      updateByRowId.set(update.rowId, update)
    }
  })
  const unknownRowIds = Array.from(updateByRowId.keys()).filter(rowId => !rowIdSet.has(rowId))
  const changedRowIds: string[] = []
  const unchangedRowIds: string[] = []
  const updatedRows = rows.map(row => {
    const update = updateByRowId.get(row.id)
    if (!update) {
      return row
    }
    const existing = getCollationRowTitle(row) || undefined
    const nextTitle = buildCollationRowTitle(update, existing, now)
    if (
      existing
      && existing.origin === nextTitle.origin
      && sameLabels(existing.labels, nextTitle.labels)
      && JSON.stringify(existing.provenance || null) === JSON.stringify(nextTitle.provenance || null)
    ) {
      unchangedRowIds.push(row.id)
      return row
    }
    changedRowIds.push(row.id)
    return {
      ...row,
      metadata: {
        ...(row.metadata || {}),
        rowTitle: nextTitle,
      },
      updatedAt: now,
    }
  })
  return { updatedRows, changedRowIds, unchangedRowIds, unknownRowIds }
}

export const clearCollationRowTitles = ({
  rows,
  rowIds,
  now = new Date().toISOString(),
}: {
  rows: AnchoredRow[]
  rowIds: string[]
  now?: string
}): CollationRowTitleApplyResult => {
  const requestedRowIds = Array.from(new Set(rowIds))
  const rowIdSet = new Set(rows.map(row => row.id))
  const unknownRowIds = requestedRowIds.filter(rowId => !rowIdSet.has(rowId))
  const requestedRowIdSet = new Set(requestedRowIds)
  const changedRowIds: string[] = []
  const unchangedRowIds: string[] = []
  const updatedRows = rows.map(row => {
    if (!requestedRowIdSet.has(row.id)) {
      return row
    }
    if (!row.metadata?.rowTitle) {
      unchangedRowIds.push(row.id)
      return row
    }
    const metadata = { ...(row.metadata || {}) }
    delete metadata.rowTitle
    changedRowIds.push(row.id)
    return {
      ...row,
      metadata: Object.keys(metadata).length ? metadata : undefined,
      updatedAt: now,
    }
  })
  return { updatedRows, changedRowIds, unchangedRowIds, unknownRowIds }
}

interface ParsedSidecarSourceRow extends CollationRowTitleSidecarRow {
  has_visible_text?: boolean
}

const sidecarRow = (value: unknown, index: number): ParsedSidecarSourceRow => {
  if (!isRecord(value)) {
    throw new Error(`Sidecar row ${index + 1} must be an object`)
  }
  const rowOrder = Number(value.row_order)
  if (!Number.isInteger(rowOrder) || rowOrder < 1) {
    throw new Error(`Sidecar row ${index + 1} has an invalid row_order`)
  }
  return {
    row_order: rowOrder,
    row_label: boundedTitleText(value.row_label, `Sidecar row ${index + 1} label`),
    ...(Number.isInteger(Number(value.source_row_order)) && Number(value.source_row_order) > 0
      ? { source_row_order: Number(value.source_row_order) }
      : {}),
    source_unit_id: cleanText(value.source_unit_id),
    title: boundedTitleText(value.title, `Sidecar row ${index + 1} title`),
    ...(typeof value.has_visible_text === 'boolean'
      ? { has_visible_text: value.has_visible_text }
      : {}),
  }
}

const sidecarScene = (value: unknown, index: number): CollationRowTitleSidecarScene => {
  if (!isRecord(value)) {
    throw new Error(`Sidecar scene ${index + 1} must be an object`)
  }
  const sceneOrder = Number(value.scene_order)
  const sourceRowOrder = Number(value.source_row_order)
  const rowStart = Number(value.row_start)
  const rowEnd = Number(value.row_end)
  if (!Number.isInteger(sceneOrder) || sceneOrder < 1) {
    throw new Error(`Sidecar scene ${index + 1} has an invalid scene_order`)
  }
  if (!Number.isInteger(sourceRowOrder) || sourceRowOrder < 1) {
    throw new Error(`Sidecar scene ${index + 1} has an invalid source_row_order`)
  }
  if (!Number.isInteger(rowStart) || rowStart < 1 || !Number.isInteger(rowEnd) || rowEnd < rowStart) {
    throw new Error(`Sidecar scene ${index + 1} has an invalid row range`)
  }
  return {
    scene_order: sceneOrder,
    source_row_order: sourceRowOrder,
    source_unit_id: cleanText(value.source_unit_id),
    title: boundedTitleText(value.title, `Sidecar scene ${index + 1} title`),
    row_start: rowStart,
    row_end: rowEnd,
    row_count: rowEnd - rowStart + 1,
    start_row_label: boundedTitleText(value.start_row_label, `Sidecar scene ${index + 1} start label`),
    end_row_label: boundedTitleText(value.end_row_label, `Sidecar scene ${index + 1} end label`),
    start_source_unit_id: cleanText(value.start_source_unit_id),
  }
}

const isMeaningfulSceneTitle = (value: string): boolean => (
  !EMPTY_SCENE_TITLES.has(cleanText(value).toLocaleLowerCase())
)

const normalizeLegacySidecarRows = ({
  chapterCode,
  sourceRows,
}: {
  chapterCode: string
  sourceRows: ParsedSidecarSourceRow[]
}): {
  rows: CollationRowTitleSidecarRow[]
  scenes: CollationRowTitleSidecarScene[]
} => {
  const hasStructuralRows = sourceRows.some(row => row.has_visible_text === false)
  if (!hasStructuralRows) {
    return {
      rows: sourceRows.map(({ has_visible_text: _hasVisibleText, ...row }) => row),
      scenes: [],
    }
  }

  let visibleOrder = 0
  const rows = sourceRows.flatMap(row => {
    if (row.has_visible_text === false) {
      return []
    }
    visibleOrder += 1
    return [{
      row_order: visibleOrder,
      row_label: `${chapterCode}.${visibleOrder}`,
      source_row_order: row.source_row_order || row.row_order,
      source_unit_id: row.source_unit_id,
      title: row.title,
    }]
  })
  const sceneStarts = sourceRows.flatMap(row => {
    if (row.has_visible_text !== false || !isMeaningfulSceneTitle(row.title)) {
      return []
    }
    const nextRow = rows.find(candidate => (
      (candidate.source_row_order || candidate.row_order) > (row.source_row_order || row.row_order)
    ))
    if (!nextRow) {
      return []
    }
    return [{
      source_row_order: row.source_row_order || row.row_order,
      source_unit_id: row.source_unit_id,
      title: row.title,
      row_start: nextRow.row_order,
      start_row_label: nextRow.row_label,
      start_source_unit_id: nextRow.source_unit_id,
    }]
  })
  const scenes = sceneStarts.map((scene, index) => {
    const nextStart = sceneStarts[index + 1]?.row_start || rows.length + 1
    const rowEnd = Math.max(scene.row_start, nextStart - 1)
    return {
      scene_order: index + 1,
      ...scene,
      row_end: rowEnd,
      row_count: rowEnd - scene.row_start + 1,
      end_row_label: `${chapterCode}.${rowEnd}`,
    }
  })
  return { rows, scenes }
}

export const parseCollationRowTitleSidecar = (value: unknown): CollationRowTitleSidecar => {
  if (!isRecord(value)) {
    throw new Error('Title sidecar must be a JSON object')
  }
  if (
    value.format_version !== COLLATION_ROW_TITLE_SIDECAR_FORMAT
    && value.format_version !== COLLATION_ROW_TITLE_SIDECAR_FORMAT_V1
  ) {
    throw new Error(`Unsupported title sidecar format: ${String(value.format_version || 'missing')}`)
  }
  const sourceHash = cleanText(value.source_html_sha256)
  if (!/^[a-f0-9]{64}$/i.test(sourceHash)) {
    throw new Error('Title sidecar source_html_sha256 must be a SHA-256 digest')
  }
  if (!Array.isArray(value.rows)) {
    throw new Error('Title sidecar rows must be an array')
  }
  const chapterCode = boundedTitleText(value.chapter_code, 'Sidecar chapter_code')
  const sourceRows = value.rows.map(sidecarRow)
  const normalized = value.format_version === COLLATION_ROW_TITLE_SIDECAR_FORMAT_V1
    ? normalizeLegacySidecarRows({ chapterCode, sourceRows })
    : {
        rows: sourceRows.map(({ has_visible_text: _hasVisibleText, ...row }) => row),
        scenes: Array.isArray(value.scenes) ? value.scenes.map(sidecarScene) : [],
      }
  return {
    format_version: value.format_version,
    generated_at: cleanText(value.generated_at) || undefined,
    source_html: cleanText(value.source_html) || undefined,
    source_html_name: boundedTitleText(value.source_html_name, 'Sidecar source_html_name'),
    source_html_sha256: sourceHash.toLowerCase(),
    chapter_code: chapterCode,
    collation_title: cleanText(value.collation_title) || undefined,
    row_count: Number.isInteger(Number(value.row_count)) ? Number(value.row_count) : undefined,
    rows: normalized.rows,
    scenes: normalized.scenes,
  }
}

const canonicalRowLabelKey = (value: unknown): string => {
  const match = cleanText(value).match(/^(.+?)[.-](\d+)$/)
  return match ? `${match[1].toLocaleLowerCase()}:${Number(match[2])}` : ''
}

const rowSourceUnitIds = (row: AnchoredRow): string[] => {
  const metadata = row.metadata || {}
  const values = [
    metadata.sourceUnitId,
    metadata.source_unit_id,
    isRecord(metadata.kalilaCollationImport) ? metadata.kalilaCollationImport.unitId : undefined,
  ]
  return Array.from(new Set(values.map(cleanText).filter(Boolean)))
}

const appendIndexValue = <Key extends string | number>(
  index: Map<Key, AnchoredRow[]>,
  key: Key,
  row: AnchoredRow,
): void => {
  if (typeof key === 'string' && !key) return
  index.set(key, [...(index.get(key) || []), row])
}

const resolveCandidates = (
  sidecarEntry: CollationRowTitleSidecarRow,
  sourceUnitRows: Map<string, AnchoredRow[]>,
  labelRows: Map<string, AnchoredRow[]>,
  orderRows: Map<number, AnchoredRow[]>,
): { method: CollationRowTitleSidecarMatchMethod; rows: AnchoredRow[] } => {
  const bySourceUnit = sourceUnitRows.get(sidecarEntry.source_unit_id) || []
  if (bySourceUnit.length) return { method: 'source-unit-id', rows: bySourceUnit }
  const byLabel = labelRows.get(canonicalRowLabelKey(sidecarEntry.row_label)) || []
  if (byLabel.length) return { method: 'row-label', rows: byLabel }
  return { method: 'row-order', rows: orderRows.get(sidecarEntry.row_order) || [] }
}

export const previewCollationRowTitleSidecar = ({
  sidecar,
  rows,
}: {
  sidecar: CollationRowTitleSidecar
  rows: AnchoredRow[]
}): CollationRowTitleSidecarPreview => {
  const emptyCounts = (): Record<CollationRowTitleSidecarMatchStatus, number> => ({
    ready: 0,
    same: 0,
    conflict: 0,
    ambiguous: 0,
    unmatched: 0,
  })
  const titleComesFromSidecar = (row: AnchoredRow): boolean => {
    const title = getCollationRowTitle(row)
    return Boolean(
      title
      && title.origin === 'import'
      && title.provenance?.sourceKind === 'html-sidecar'
      && title.provenance.sourceHash === sidecar.source_html_sha256
    )
  }
  const sceneForRow = (row: AnchoredRow): AnchoredRowSceneMetadata | null => {
    const scene = row.metadata?.scene
    return scene && typeof scene === 'object' && !Array.isArray(scene) ? scene : null
  }
  const sceneComesFromSidecar = (scene: AnchoredRowSceneMetadata): boolean => (
    scene.source === 'html-sidecar'
    && scene.sourceHash === sidecar.source_html_sha256
  )
  const sourceUnitRows = new Map<string, AnchoredRow[]>()
  const labelRows = new Map<string, AnchoredRow[]>()
  const orderRows = new Map<number, AnchoredRow[]>()
  rows.forEach(row => {
    rowSourceUnitIds(row).forEach(sourceUnitId => appendIndexValue(sourceUnitRows, sourceUnitId, row))
    appendIndexValue(labelRows, canonicalRowLabelKey(`${row.labelPrefix}-${row.labelNumber}`), row)
    appendIndexValue(orderRows, row.labelNumber, row)
  })

  const matches = sidecar.rows.map(sidecarEntry => {
    const resolved = resolveCandidates(sidecarEntry, sourceUnitRows, labelRows, orderRows)
    if (!resolved.rows.length) {
      return {
        sidecarRow: sidecarEntry,
        status: 'unmatched' as const,
        method: null,
        rowId: null,
        candidateRowIds: [],
        currentTitle: '',
        safeToReplace: false,
      }
    }
    if (resolved.rows.length > 1) {
      return {
        sidecarRow: sidecarEntry,
        status: 'ambiguous' as const,
        method: resolved.method,
        rowId: null,
        candidateRowIds: resolved.rows.map(row => row.id),
        currentTitle: '',
        safeToReplace: false,
      }
    }
    const row = resolved.rows[0]
    const currentTitle = getCollationRowPrimaryTitle(row)
    const status: CollationRowTitleSidecarMatchStatus = !currentTitle
      ? 'ready'
      : cleanText(currentTitle) === cleanText(sidecarEntry.title)
        ? 'same'
        : 'conflict'
    return {
      sidecarRow: sidecarEntry,
      status,
      method: resolved.method,
      rowId: row.id,
      candidateRowIds: [row.id],
      currentTitle,
      safeToReplace: status === 'conflict' && titleComesFromSidecar(row),
    }
  })

  const counts = emptyCounts()
  matches.forEach(match => {
    counts[match.status] += 1
  })

  const sortedRows = [...rows].sort((left, right) => {
    if (left.position !== right.position) {
      return left.position - right.position
    }
    return left.labelNumber - right.labelNumber
  })
  const sortedIndexByRowId = new Map(sortedRows.map((row, index) => [row.id, index]))
  const resolveSceneBoundary = ({
    sourceUnitId,
    rowLabel,
    rowOrder,
  }: {
    sourceUnitId?: string
    rowLabel: string
    rowOrder: number
  }): { method: CollationRowTitleSidecarMatchMethod; rows: AnchoredRow[] } => {
    const bySourceUnit = sourceUnitId ? sourceUnitRows.get(sourceUnitId) || [] : []
    if (bySourceUnit.length) {
      return { method: 'source-unit-id', rows: bySourceUnit }
    }
    const byLabel = labelRows.get(canonicalRowLabelKey(rowLabel)) || []
    if (byLabel.length) {
      return { method: 'row-label', rows: byLabel }
    }
    return { method: 'row-order', rows: orderRows.get(rowOrder) || [] }
  }
  const sceneMatches = sidecar.scenes.map(sidecarSceneEntry => {
    const start = resolveSceneBoundary({
      sourceUnitId: sidecarSceneEntry.start_source_unit_id,
      rowLabel: sidecarSceneEntry.start_row_label,
      rowOrder: sidecarSceneEntry.row_start,
    })
    const end = resolveSceneBoundary({
      rowLabel: sidecarSceneEntry.end_row_label,
      rowOrder: sidecarSceneEntry.row_end,
    })
    const method = start.method === end.method ? start.method : 'mixed' as const
    if (!start.rows.length || !end.rows.length) {
      return {
        sidecarScene: sidecarSceneEntry,
        status: 'unmatched' as const,
        method: null,
        rowIds: [],
        startRowId: null,
        endRowId: null,
        currentTitles: [],
        safeToReplace: false,
      }
    }
    if (start.rows.length > 1 || end.rows.length > 1) {
      return {
        sidecarScene: sidecarSceneEntry,
        status: 'ambiguous' as const,
        method,
        rowIds: [],
        startRowId: null,
        endRowId: null,
        currentTitles: [],
        safeToReplace: false,
      }
    }
    const startRow = start.rows[0]
    const endRow = end.rows[0]
    const startIndex = sortedIndexByRowId.get(startRow.id)
    const endIndex = sortedIndexByRowId.get(endRow.id)
    if (startIndex == null || endIndex == null || endIndex < startIndex) {
      return {
        sidecarScene: sidecarSceneEntry,
        status: 'unmatched' as const,
        method,
        rowIds: [],
        startRowId: startRow.id,
        endRowId: endRow.id,
        currentTitles: [],
        safeToReplace: false,
      }
    }
    const sceneRows = sortedRows.slice(startIndex, endIndex + 1)
    const currentScenes = sceneRows
      .map(sceneForRow)
      .filter((scene): scene is AnchoredRowSceneMetadata => Boolean(scene))
    const currentTitles = Array.from(new Set(
      currentScenes.map(scene => cleanText(scene.title)).filter(Boolean)
    ))
    const allRowsMatch = currentScenes.length === sceneRows.length
      && currentTitles.length === 1
      && currentTitles[0] === cleanText(sidecarSceneEntry.title)
    const status: CollationRowTitleSidecarMatchStatus = currentScenes.length === 0
      ? 'ready'
      : allRowsMatch
        ? 'same'
        : 'conflict'
    return {
      sidecarScene: sidecarSceneEntry,
      status,
      method,
      rowIds: sceneRows.map(row => row.id),
      startRowId: startRow.id,
      endRowId: endRow.id,
      currentTitles,
      safeToReplace: status === 'conflict' && currentScenes.every(sceneComesFromSidecar),
    }
  })
  const sceneCounts = emptyCounts()
  sceneMatches.forEach(match => {
    sceneCounts[match.status] += 1
  })

  const canClearStaleImports = (
    counts.ambiguous + counts.unmatched + sceneCounts.ambiguous + sceneCounts.unmatched
  ) === 0
  const targetTitleRowIds = new Set(matches.flatMap(match => match.rowId ? [match.rowId] : []))
  const targetSceneRowIds = new Set(sceneMatches.flatMap(match => match.rowIds))
  const staleImportedTitleRowIds = canClearStaleImports
    ? rows.filter(row => titleComesFromSidecar(row) && !targetTitleRowIds.has(row.id)).map(row => row.id)
    : []
  const staleImportedSceneRowIds = canClearStaleImports
    ? rows.filter(row => {
        const scene = sceneForRow(row)
        return Boolean(scene && sceneComesFromSidecar(scene) && !targetSceneRowIds.has(row.id))
      }).map(row => row.id)
    : []

  return {
    sidecar,
    matches,
    counts,
    sceneMatches,
    sceneCounts,
    repairableTitleCount: matches.filter(match => (
      match.status === 'ready' || (match.status === 'conflict' && match.safeToReplace)
    )).length,
    repairableSceneCount: sceneMatches.filter(match => (
      match.status === 'ready' || (match.status === 'conflict' && match.safeToReplace)
    )).length,
    staleImportedTitleRowIds,
    staleImportedSceneRowIds,
    canClearStaleImports,
  }
}

export const buildSidecarTitleUpdates = ({
  preview,
  matchStatuses = ['ready'],
  rowIds,
  updatedBy,
}: {
  preview: CollationRowTitleSidecarPreview
  matchStatuses?: CollationRowTitleSidecarMatchStatus[]
  rowIds?: string[]
  updatedBy?: string
}): CollationRowTitleUpdate[] => {
  const acceptedStatuses = new Set(matchStatuses)
  const acceptedRowIds = rowIds ? new Set(rowIds) : null
  return preview.matches
    .filter(match => (
      Boolean(match.rowId)
      && acceptedStatuses.has(match.status)
      && (!acceptedRowIds || acceptedRowIds.has(match.rowId as string))
    ))
    .map(match => ({
      rowId: match.rowId as string,
      primaryText: match.sidecarRow.title,
      primaryLanguage: 'en',
      origin: 'import' as const,
      provenance: {
        sourceKind: 'html-sidecar' as const,
        sourceName: preview.sidecar.source_html_name,
        sourceHash: preview.sidecar.source_html_sha256,
        sourceUnitId: match.sidecarRow.source_unit_id || undefined,
      },
      updatedBy,
    }))
}

export const applyRoleAwareSidecarRepair = ({
  preview,
  rows,
  updatedBy,
  applyScenes = true,
  now = new Date().toISOString(),
}: {
  preview: CollationRowTitleSidecarPreview
  rows: AnchoredRow[]
  updatedBy?: string
  applyScenes?: boolean
  now?: string
}): CollationRowTitleSidecarRepairResult => {
  const changedRowIds = new Set<string>()
  const clearedTitleRowIds = preview.canClearStaleImports
    ? preview.staleImportedTitleRowIds
    : []
  let updatedRows = rows
  if (clearedTitleRowIds.length) {
    const cleared = clearCollationRowTitles({
      rows: updatedRows,
      rowIds: clearedTitleRowIds,
      now,
    })
    updatedRows = cleared.updatedRows
    cleared.changedRowIds.forEach(rowId => changedRowIds.add(rowId))
  }

  const repairableTitleMatches = preview.matches.filter(match => (
    Boolean(match.rowId)
    && (
      match.status === 'ready'
      || (match.status === 'conflict' && match.safeToReplace)
    )
  ))
  const appliedTitles = applyCollationRowTitleUpdates({
    rows: updatedRows,
    updates: repairableTitleMatches.map(match => ({
      rowId: match.rowId as string,
      primaryText: match.sidecarRow.title,
      primaryLanguage: 'en',
      origin: 'import' as const,
      provenance: {
        sourceKind: 'html-sidecar' as const,
        sourceName: preview.sidecar.source_html_name,
        sourceHash: preview.sidecar.source_html_sha256,
        sourceUnitId: match.sidecarRow.source_unit_id || undefined,
      },
      updatedBy,
    })),
    now,
  })
  updatedRows = appliedTitles.updatedRows
  appliedTitles.changedRowIds.forEach(rowId => changedRowIds.add(rowId))

  const repairableSceneMatches = applyScenes ? preview.sceneMatches.filter(match => (
    match.rowIds.length > 0
    && (
      match.status === 'ready'
      || (match.status === 'conflict' && match.safeToReplace)
    )
  )) : []
  const desiredSceneByRowId = new Map<string, AnchoredRowSceneMetadata>()
  repairableSceneMatches.forEach(match => {
    match.rowIds.forEach((rowId, index) => {
      desiredSceneByRowId.set(rowId, {
        sceneId: `html-sidecar:${preview.sidecar.source_html_sha256.slice(0, 12)}:${match.sidecarScene.scene_order}`,
        sceneOrdinal: match.sidecarScene.scene_order,
        title: match.sidecarScene.title,
        rowStart: match.sidecarScene.row_start,
        rowEnd: match.sidecarScene.row_end,
        rowCount: match.sidecarScene.row_count,
        isSceneStart: index === 0,
        source: 'html-sidecar',
        sourceName: preview.sidecar.source_html_name,
        sourceHash: preview.sidecar.source_html_sha256,
        sourceUnitId: match.sidecarScene.source_unit_id,
        sourceRowOrder: match.sidecarScene.source_row_order,
      })
    })
  })
  const staleSceneRowIdSet = new Set(
    applyScenes && preview.canClearStaleImports ? preview.staleImportedSceneRowIds : []
  )
  const appliedSceneRowIds: string[] = []
  const clearedSceneRowIds: string[] = []
  updatedRows = updatedRows.map(row => {
    const desiredScene = desiredSceneByRowId.get(row.id)
    if (desiredScene) {
      if (JSON.stringify(row.metadata?.scene || null) === JSON.stringify(desiredScene)) {
        return row
      }
      appliedSceneRowIds.push(row.id)
      changedRowIds.add(row.id)
      return {
        ...row,
        metadata: {
          ...(row.metadata || {}),
          scene: desiredScene,
        },
        updatedAt: now,
      }
    }
    if (!staleSceneRowIdSet.has(row.id) || !row.metadata?.scene) {
      return row
    }
    const metadata = { ...(row.metadata || {}) }
    delete metadata.scene
    clearedSceneRowIds.push(row.id)
    changedRowIds.add(row.id)
    return {
      ...row,
      metadata: Object.keys(metadata).length ? metadata : undefined,
      updatedAt: now,
    }
  })

  return {
    updatedRows,
    changedRowIds: Array.from(changedRowIds),
    appliedTitleRowIds: appliedTitles.changedRowIds,
    clearedTitleRowIds,
    appliedSceneRowIds,
    clearedSceneRowIds,
  }
}
